import { autoinject, bindable, computedFrom, ViewEngine } from "aurelia-framework";
import { transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { Container, View } from "aurelia-framework";
import { Reflection } from "../../../utilities/reflection";

@autoinject
@transient()
export class ButtonMultiSelector {

    private _scrollViewerTop: number;
    private _invertDropdown: boolean;

    public indexFocusedLi: number = 0;
    public dropdownElement: any;
    public currentContext: ButtonMultiSelector = this;
    private itemSourceAsEnumerable: Enumerable.IEnumerable<any>;
    private subscriptionToDocumentClickEvent: Subscription;
    public input: any;
    public buttonElement: Element;
    public dropdown: View;

    @bindable
    public label: string;

    @bindable
    public type: string;

    @bindable
    public disabled: boolean;

    @bindable
    public loading: boolean;

    @bindable
    public ripple: boolean = true;

    @bindable
    public tab: number;

    @bindable
    public showOnAdorner: boolean;

    @bindable
    public selectedItem: any;

    @bindable
    public selectedValue: number;

    @bindable
    public selectedValuePath: string;

    @bindable
    public isDropdownOpen: boolean;

    @bindable
    public displayMemberPath: string;

    @bindable
    public itemSource: any[];

    @bindable
    public setDefault: boolean;

    @bindable
    public autoWidthDropdown: boolean;

    @bindable
    public dropdownDirection: 'auto' | 'up' | 'down' = 'auto';

    constructor(private element: Element,
        private eventAggrigator: EventAggregator,
        private viewEngine: ViewEngine) {
    }

    detached() {
        if (this.subscriptionToDocumentClickEvent) {
            this.subscriptionToDocumentClickEvent.dispose();
        }
    }

    closePopup = () => {
        this.isDropdownOpen = false;
    }

    public itemSourceChanged(newValue: any[], oldValue: any[]): void {

        if (newValue === oldValue) {
            return;
        }
        this.itemSourceAsEnumerable = Enumerable.from<any>(this.itemSource);

        if (newValue && this.selectedValue) {
            const selectedValueStringified = JSON.stringify(this.selectedValue);
            const condition = x => JSON.stringify(this.getValue(x)) == selectedValueStringified || this.getValue(x) == this.selectedValue;
            this.selectedItem = this.itemSourceAsEnumerable.firstOrDefault(condition);
        }

        if (this.setDefault && !this.selectedItem) {
            this.select(this.itemSourceAsEnumerable.firstOrDefault());
        }
    }

    public setDefaultChanged(): void {
        if (this.setDefault && this.itemSource && !this.selectedItem && !this.selectedValue) {
            this.select(Enumerable.from<any>(this.itemSource).firstOrDefault());
        }
    }

    public selectedValueChanged(newValue: number, oldValue: number): void {
        if (newValue === oldValue) {
            return;
        }

        if (this.itemSource) {
            this.selectedItem = Enumerable.from<any>(this.itemSource)
                .firstOrDefault(x => this.getValue(x) == newValue);
            this.indexFocusedLi = this.itemSource.indexOf(this.selectedItem) % this.itemSource.length;
        }
    }

    public selectedItemChanged(newValue: number, oldValue: number): void {
        if (newValue === oldValue) {
            return;
        }
        this.selectedValue = this.getValue(newValue);
    }

    public focusTextBox(selectAlltext: boolean = false): void {
        const input: JQuery = this.input;

        if (input) {
            setTimeout(() => {
                input.focus();
                if (selectAlltext) {
                    input.select();
                }
            });
        }
    }

    stopPropagation(e: Event): void {
        e.stopPropagation();
    }

    public getDisplayName(item: any): string {
        return this.displayMemberPath && item ? item[this.displayMemberPath] : item;
    }

    public select(item: any): void {
        if (this.loading || this.disabled)
            return;

        if (item) {
            this.selectedItem = item;
            this.selectedValue = this.getValue(item);
            this.isDropdownOpen = false;
            this.onSelect();
        }
    }

    private onSelect(): void {
        if (this.loading || this.disabled)
            return;

        setTimeout(() => {
            if (this.selectedItem) {
                const paramsEvent: any = { detail: this.selectedItem, bubbles: true };
                const changeEvent: CustomEvent = new CustomEvent("item-selected", paramsEvent);
                this.element.dispatchEvent(changeEvent);
            }
        });
    }

    public getValue(item: any): any {
        return this.selectedValuePath && item ? item[this.selectedValuePath] : item;
    }

    attached(): void {
        this.subscriptionToDocumentClickEvent = this.eventAggrigator.subscribe(Messages.documentClick, this.closePopup);
        // disables TabIndex if Disabled
        if (this.disabled) {
            this.tab = -1;
        }

        this.viewEngine.loadViewFactory('views/components/button-multi-selector/button-multi-selector-dropdown.html').then(response => {
            this.dropdown = response.create(Container.instance);
        });
    }

    @computedFrom(Reflection.getPropertyPath<ButtonMultiSelector>(z => z.dropdownDirection),
        Reflection.getPropertyPath<ButtonMultiSelector>(z => z.dropdownElement))  
    get invertDropdown(): boolean {
        if (this.dropdownDirection != 'auto')
            return this.dropdownDirection == 'up';

        if (!this.dropdownElement)
            return this._invertDropdown;

        const scrollViewer = this.getScrollParent(this.dropdownElement);

        if (scrollViewer) {
            if (!this._scrollViewerTop) {
                this._scrollViewerTop = this.getTop(scrollViewer);
            }

            const dropdownPosition = ((this.getTop($(this.dropdownElement).parent().get(0)) + $(this.dropdownElement).parent().get(0).clientHeight) - this._scrollViewerTop) + this.dropdownElement.clientHeight;

            if (scrollViewer.nodeType != document.DOCUMENT_FRAGMENT_NODE && scrollViewer.clientHeight) {
                this._invertDropdown = scrollViewer.clientHeight < dropdownPosition;
            }
        }

        return this._invertDropdown;
    }

    private getTop(elem: HTMLElement): number {
        if (!elem || !elem.getBoundingClientRect) {
            return 0;
        }

        const rect = elem.getBoundingClientRect();
        const doc = elem.ownerDocument;
        const docElem = doc.documentElement;
        const win = doc.defaultView;
        return rect.top + win.pageYOffset - docElem.clientTop;
    }

    getScrollParent(node) {
        if (node === null) {
            return null;
        }

        if (this.hasScroll(node)) {
            return node;
        } else {
            return this.getScrollParent(node.parentNode);
        }
    }

    hasScroll(el: any): boolean {
        if (el.nodeType != document.ELEMENT_NODE) {
            return true;
        }

        const $el = $(el),
            sY = $el.css("overflow-y"),
            hidden = "hidden", // minifiers would like this better
            visible = "visible",
            scroll = "scroll";

        if (sY === hidden || sY === visible) { return false; }
        if (sY === scroll) { return true; }

        // compare client and scroll dimensions to see if a scrollbar is needed

        return $el.innerHeight() < el.scrollHeight || // make use of potential short circuit
            $el.innerWidth() < el.scrollWidth;
    }

    click(e: Event): void {
        if (this.isDropdownOpen) {
            this.closePopup();
            return;
        }
        if (this.loading || this.disabled)
            return;

        this.eventAggrigator.publish(Messages.documentClick, { source: "multi-button-selector" });
        e.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    disabledChanged(): void {
        if (this.disabled) {
            this.tab = -1;
        } else {
            this.tab = 0;
        }
    }
}
